import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FirebaseService } from 'src/app/firebase/firebase.service';
import { IConnection } from '../models/Progress';
import { SubSink } from 'subsink';
import { IChapter, ICourse, IELearningData, IMasterclassVersion, IModule } from '@shared';
import { MasterclassService } from '../masterclasses/masterclass.service';
import { filterVisibleChapters, getChaptersCourses, getFinalQuizCourse, isDeliverableCourse } from '../e-learning-helpers';
import { Observable, combineLatest, forkJoin, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-course',
  templateUrl: './course.component.html',
  styleUrls: ['./course.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CourseComponent implements OnInit, OnDestroy {
  masterclassVersion$: Observable<IMasterclassVersion>;
  innerWidth = window.innerWidth;

  module: IModule | null = null;
  masterclassId: string = '';
  chapters: IChapter[] = [];
  selectedCourse = this.chapters[0]?.courses[0];
  selectedChapterId: string = '';
  isSelectedCourseLast = false;
  isVisualize = false;
  connectionInfo: IConnection = { start: new Date(), end: new Date() };

  baseUrl = '';
  loading = true;

  subs = new SubSink();

  constructor(private router: Router, private firebaseService: FirebaseService, private activatedRoute: ActivatedRoute, private masterclassService: MasterclassService) {
    const urlSplit = this.router.url.split('/');

    const masterclassId = urlSplit[urlSplit.indexOf('masterclasses') + 1];
    this.masterclassId = masterclassId;
    const moduleId = urlSplit[urlSplit.indexOf('modules') + 1];
    const modulesIndex = this.router.url.indexOf('/modules/');
    this.baseUrl = modulesIndex !== -1
      ? this.router.url.substring(0, modulesIndex + '/modules/'.length)
      : this.router.url.split(moduleId)[0];

    if (urlSplit.includes('visualize')) {
      this.isVisualize = true;
      const versionId = urlSplit[urlSplit.indexOf('visualize') + 1];
      this.masterclassVersion$ = this.masterclassService.getMasterclassVersion(versionId);
      this.subs.sink = this.masterclassVersion$.subscribe({
        next: (masterclassVersion) => {
          const mc = masterclassVersion.masterclasses.find((mc) => mc.id === masterclassId);
          this.module = mc?.modules.find((md) => md.id === moduleId);
          this.loadChapters();
        },
        error: (err) => {
          this.loading = false;
        }
      });
    } else {
      this.subs.sink = combineLatest([this.firebaseService.getRemoteConfig(), this.masterclassService.getObtainedMasterclasses$()])
        .pipe(
          switchMap(([remoteConfig, obtainedMasterclasses]) => {
            const selectedELearningVersion = remoteConfig.parameters['selectedELearningVersion'].defaultValue.value;
            this.masterclassVersion$ = this.masterclassService.getMasterclassVersion(selectedELearningVersion).pipe(
              tap((mcv) => {
                if (!(mcv.id === remoteConfig.parameters['selectedELearningVersion'].defaultValue.value)) {
                  this.router.navigate(['home']);
                }
                const mc = mcv.masterclasses.find((mc) => mc.id === masterclassId);
                this.module = mc?.modules.find((module) => module.id === moduleId);
                if (!obtainedMasterclasses.includes(masterclassId) && !this.module?.isAvailableInDemo) {
                  this.router.navigate(['home']);
                } else {
                  this.loading = false;
                  this.loadChapters();
                }
              })
            );

            return this.masterclassVersion$;
          })
        )
        .subscribe();
    }
  }

  loadChapters(): void {
    this.chapters = this.module?.chapters;

    if (!this.chapters) {
      this.loading = false;
      this.router.navigate(['home']);
      return;
    }
  
    document.onvisibilitychange = async () => {
      if (document.visibilityState === 'hidden') {
        this.connectionInfo.end = new Date();
        await this.firebaseService.registerELearningConnection(this.connectionInfo);
      } else {
        this.connectionInfo.start = new Date();
      }
    };
  
    const urlSplit = this.router.url.split('/');
    const courseId = decodeURIComponent(urlSplit.pop() ?? '');
    const chapterId = decodeURIComponent(urlSplit.pop() ?? '');

    this.subs.sink = this.firebaseService.getELearningData().subscribe({
      next: (progress: IELearningData) => {
        if (!this.isVisualize) {
          this.chapters = filterVisibleChapters(this.module.chapters, progress?.trainingType);
        }
        if (!this.chapters.length) {
          this.loading = false;
          this.navigateToModules();
          return;
        }
        this.chapters.forEach((chapter) => {
          chapter.courses.forEach((course) => {
            course.isCompleted = progress?.completedCourses?.includes(course.id) ?? false;
          });
        });
        this.selectCourse(chapterId, courseId);
        this.scrollSelectedCourseIntoView();
        this.loading = false;
      },
      error: (err) => {
        this.selectCourse(chapterId, courseId);
        this.scrollSelectedCourseIntoView();
        this.loading = false;
      }
    });
  }


  ngOnInit(): void {
  }

  private scrollSelectedCourseIntoView(): void {
    setTimeout(() => document.querySelector('.course-selected')?.scrollIntoView({ block: 'center' }));
  }

  selectCourse(selectedChapterId: string, courseId?: string): void {
    let selectedChapter = this.chapters.find((chapter) => chapter.id === selectedChapterId);
    let course = selectedChapter?.courses.find((course) => course.id === courseId);
    if (!selectedChapter) {
      this.router.navigate([`${this.baseUrl}${this.module?.id}/${this.chapters[0]?.id}/${this.chapters[0]?.courses?.[0]?.id}`]);
      course = this.chapters[0]?.courses?.[0];
      selectedChapter = this.chapters[0];
    } else if (!course) {
      this.router.navigate([`${this.baseUrl}${this.module?.id}/${selectedChapter.id}/${selectedChapter.courses?.[0]?.id}`]);
      course = selectedChapter.courses?.[0];
    } else {
      this.router.navigate([`${this.baseUrl}${this.module.id}/${selectedChapter.id}/${course.id}`]);
    }
    this.selectedCourse = course;
    this.selectedChapterId = selectedChapter.id;
    this.isSelectedCourseLast = this.isLastCourse(selectedChapter, this.selectedCourse);
  }

  chapterCompletion(chapter: IChapter) {
    const completedCourses = chapter.courses.filter((course) => course.isCompleted).length;
    return Math.round((completedCourses / chapter.courses.length) * 100);
  }

  goToNextCourse() {
    this.firebaseService
      .registerELearningProgress(this.selectedCourse.id)
      .then(() => {
        this.chapters.forEach((chapter) =>
          chapter.courses.forEach((course) => {
            if (course.id === this.selectedCourse.id) {
              course.isCompleted = true;
            }
          })
        );
        if (this.isSelectedCourseLast) {
          this.router.navigate([`${this.baseUrl}`]);
          return;
        }
        const selectedChapter = this.chapters.find((chapter) => chapter.id === this.selectedChapterId);
        if (!selectedChapter) {
          return;
        }
        const courseIndex = selectedChapter.courses.findIndex((course) => course.id === this.selectedCourse.id);
        if (courseIndex < selectedChapter.courses.length - 1) {
          const newCourse = selectedChapter.courses[courseIndex + 1];
          this.selectCourse(selectedChapter.id, newCourse.id);
        } else {
          const selectedChapterIndex = this.chapters.indexOf(selectedChapter);
          const newChapter = this.chapters[selectedChapterIndex + 1];
          this.selectCourse(newChapter.id);
        }
      })
      .catch((err) => console.error(err));
  }

  isLastCourse(selectedChapter: IChapter, selectedCourse: ICourse) {
    return (
      this.chapters.findIndex((chapter) => chapter.id === selectedChapter?.id) === this.chapters.length - 1 &&
      selectedChapter?.courses.findIndex((course) => course.id === selectedCourse.id) === selectedChapter?.courses.length - 1
    );
  }

  navigateToModules(): void {
    this.router.navigate([this.baseUrl]);
  }

  onCourseCompleted(): void {
    this.chapters.forEach((chapter) => {
      chapter.courses.forEach((course) => {
        if (!course.isCompleted) {
          course.isCompleted = true;
          this.firebaseService.registerELearningProgress(course.id);
        }
      });
    });
  }

  async onQuizSubmitted(courseId: string): Promise<void> {
    if (getFinalQuizCourse(this.module)?.id !== courseId) {
      return;
    }
    const coursesToComplete = getChaptersCourses(this.chapters).filter((course) => !course.isCompleted && !isDeliverableCourse(course));
    coursesToComplete.forEach((course) => {
      course.isCompleted = true;
    });
    await Promise.all(coursesToComplete.map((course) => this.firebaseService.registerELearningProgress(course.id)));
  }

  ngOnDestroy(): void {
    document.onvisibilitychange = null;
    this.subs.unsubscribe();
  }
}
